import {useMemo, useRef} from 'react';
import { Checkbox, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import {BulkActionsTableProps} from './BulkActionsTable.props';
import {INITIAL, PENDING, POLLING} from '../../../../../../hooks/useRollingApiRequest/useRollingApiRequest.props';
import {typedObjectValues} from '../../../../../../utilities';
import {columnHeaders} from '../../constants';
import ResultStatusIcon from '../ResultStatusIcon/ResultStatusIcon';

const BulkActionsTable = ({
  selectionModelState: {
    value: selectionModel,
    setter: setSelectionModel
  },
  rows,
  visibleColumns,
  visibleSubColumns,
  saveStatus,
  saveResults,
  defaultRowConfig,
}: BulkActionsTableProps) => {

  const hasInitializedUpdate = useMemo(() => saveStatus !== INITIAL, [saveStatus])

  const fullfilledPromises = useMemo(() => {
    const listSaveResults = typedObjectValues(saveResults)
    const completedResults = listSaveResults.filter(data => ![PENDING, POLLING].includes(data.state))
    return `${completedResults.length}/${listSaveResults.length}`
  }, [saveResults])

  const selectionModelSet = useMemo(() => new Set(selectionModel), [selectionModel]);

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 53,
    overscan: 5,
    getItemKey: index => rows[index]?.id ?? index,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const firstVirtualItem = virtualItems[0];
  const lastVirtualItem = virtualItems[virtualItems.length - 1];
  const paddingTop = firstVirtualItem?.start ?? 0;
  const paddingBottom = totalSize - (lastVirtualItem?.end ?? 0);

  const columnCount = useMemo(() => {
    if (rows.length === 0) {
      return 2 + (hasInitializedUpdate ? 1 : 0);
    }
    const firstRow = rows[0];
    return 1 + 
           1 + 
           (firstRow.rowCells?.filter(rowCell => rowCell.isVisible).length ?? 0) +
           (hasInitializedUpdate ? 1 : 0);
  }, [rows, hasInitializedUpdate]);

  return (
    <TableContainer 
      ref={parentRef}
      style={{ maxHeight: 400, overflow: 'auto' }}
    >
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell />
            {defaultRowConfig.filter(column => column.isVisible).map(column => (
              <TableCell key={column.field}>
                {column.renderCell()}
              </TableCell>
            ))}
            {hasInitializedUpdate && (<TableCell>
              <Chip label={fullfilledPromises} color="primary"/>
            </TableCell>)}
          </TableRow>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selectionModel.length > 0 && selectionModel.length < rows.length}
                checked={rows.length > 0 && selectionModel.length === rows.length}
                onChange={event => {
                  if (event.target.checked) {
                    const newSelectionModel = rows.map(row => row.id);
                    setSelectionModel(newSelectionModel);
                  } else {
                    setSelectionModel([]);
                  }
                }}
              />
            </TableCell>
            {columnHeaders
              .filter(columnHeader => visibleColumns.includes(columnHeader.field))
              .map(column => {
                if (column.subColumns.length > 0) {
                  return column.subColumns.filter(subColumn => visibleSubColumns.find(visibleSubCol => visibleSubCol.field === subColumn.field)).map(subColumn => (
                    <TableCell key={`${column.field}-${subColumn.field}`}>
                      <strong>{subColumn.headerName}</strong>
                    </TableCell>
                  ))
                }
                return (
                  <TableCell key={column.field}>
                    <strong>{column.headerName}</strong>
                  </TableCell>
                );
              })}
            {hasInitializedUpdate && (<TableCell/>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {paddingTop > 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} style={{ padding: 0, height: paddingTop, border: 0, lineHeight: 0 }} />
            </TableRow>
          )}
          
          {virtualItems.map(virtualRow => {
            const row = rows[virtualRow.index];
            return (
              <TableRow
                key={virtualRow.key}
                hover
                selected={selectionModelSet.has(row.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectionModelSet.has(row.id)}
                    onChange={event => {
                      if (event.target.checked) {
                        setSelectionModel(prev => [...prev, row.id]);
                      } else {
                        setSelectionModel(prev => prev.filter(id => id !== row.id));
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  {row.company_name}
                </TableCell>
                {row.rowCells.filter(rowCell => rowCell.isVisible).map(rowCell => (
                  <TableCell key={`${row.id}-${rowCell.field}`}>
                    {rowCell.renderCell()}
                  </TableCell>
                ))}
                {hasInitializedUpdate && (
                  <TableCell>
                    {saveResults[row.valuation_id] && (
                      <ResultStatusIcon 
                        state={saveResults[row.valuation_id].state} 
                        errorMsg={saveResults[row.valuation_id].errorMsg}
                      />
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {paddingBottom > 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} style={{ padding: 0, height: paddingBottom, border: 0, lineHeight: 0 }} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default BulkActionsTable;